import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  Send, Paperclip, Loader2, MessageSquare, User, ShieldCheck, Search, Plus, FileIcon, Download, X, ArrowLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

export default function MessagingPage() {
  const { user, isAdmin } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [msgLoading, setMsgLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [newConvoDialog, setNewConvoDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mobileShowThread, setMobileShowThread] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchConversations(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/conversations`);
      setConversations(res.data);
      // Auto-open first or only conversation for clients
      if (!isAdmin && res.data.length === 1 && !activeConvo) {
        openConversation(res.data[0]);
      }
    } catch (err) {
      console.error("Error fetching conversations:", err);
    } finally { setLoading(false); }
  };

  const openConversation = async (convo) => {
    setActiveConvo(convo);
    setMobileShowThread(true);
    setMsgLoading(true);
    try {
      const res = await axios.get(`${API}/conversations/${convo.id}/messages`);
      setMessages(res.data);
      // Update local unread count
      setConversations(prev => prev.map(c =>
        c.id === convo.id ? { ...c, [isAdmin ? "unread_admin" : "unread_client"]: 0 } : c
      ));
    } catch { toast.error("Erreur chargement messages"); }
    finally { setMsgLoading(false); }
  };

  const handleSend = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;
    if (!activeConvo) return;
    setSending(true);
    try {
      await axios.post(`${API}/conversations/${activeConvo.id}/messages`, {
        content: newMessage,
        attachments,
      });
      setNewMessage("");
      setAttachments([]);
      // Refresh messages and conversation list
      const [msgRes, convoRes] = await Promise.all([
        axios.get(`${API}/conversations/${activeConvo.id}/messages`),
        axios.get(`${API}/conversations`),
      ]);
      setMessages(msgRes.data);
      setConversations(convoRes.data);
    } catch { toast.error("Erreur envoi"); }
    finally { setSending(false); }
  };

  const handleNewConvo = async () => {
    if (!selectedClient) return;
    try {
      const res = await axios.post(`${API}/conversations?client_id=${selectedClient}`);
      setNewConvoDialog(false);
      setSelectedClient("");
      await fetchConversations();
      openConversation(res.data);
    } catch { toast.error("Erreur creation conversation"); }
  };

  const handleClientNewConvo = async () => {
    try {
      const res = await axios.post(`${API}/conversations`);
      await fetchConversations();
      openConversation(res.data);
      toast.success("Conversation creee avec l'equipe GLOTECH");
    } catch { toast.error("Erreur creation conversation"); }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch { console.error("Error fetching clients"); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeConvo) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/conversations/${activeConvo.id}/upload-attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments(prev => [...prev, res.data]);
      toast.success("Fichier attache");
    } catch { toast.error("Erreur upload"); }
    finally { setUploading(false); fileInputRef.current.value = ""; }
  };

  const removeAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const formatDate = (d) => {
    if (!d) return "";
    const date = new Date(d);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  };

  const formatFullDate = (d) => new Date(d).toLocaleDateString("fr-FR", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

  const filteredConvos = conversations.filter(c =>
    !searchQuery || (c.client_name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const unreadKey = isAdmin ? "unread_admin" : "unread_client";

  return (
    <div className="flex h-[calc(100vh-80px)] animate-fade-in" data-testid="messaging-page">
      {/* Sidebar - Conversations List */}
      <div className={`${mobileShowThread ? "hidden md:flex" : "flex"} flex-col w-full md:w-[340px] border-r border-[#2A3441] flex-shrink-0`}>
        <div className="p-4 border-b border-[#2A3441]">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-lg font-bold text-white">Messagerie</h1>
            {isAdmin ? (
              <Button size="sm" onClick={() => { setNewConvoDialog(true); fetchClients(); }}
                className="bg-[#0099CC] hover:bg-[#0088B5] text-white h-8" data-testid="new-conversation-btn">
                <Plus className="h-4 w-4 mr-1" /> Nouveau
              </Button>
            ) : (
              conversations.length === 0 && (
                <Button size="sm" onClick={handleClientNewConvo}
                  className="bg-[#0099CC] hover:bg-[#0088B5] text-white h-8" data-testid="client-new-conversation-btn">
                  <Plus className="h-4 w-4 mr-1" /> Contacter
                </Button>
              )
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748b]" />
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher..." className="pl-9 bg-[#0B0F19] border-[#2A3441] text-white h-9 text-sm"
              data-testid="search-conversations" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#0099CC]" /></div>
          ) : filteredConvos.length === 0 ? (
            <div className="text-center py-10 px-4">
              <MessageSquare className="h-10 w-10 text-[#64748b] mx-auto mb-3" />
              <p className="text-[#94A3B8] text-sm">Aucune conversation</p>
              {!isAdmin && (
                <Button size="sm" onClick={handleClientNewConvo}
                  className="bg-[#0099CC] hover:bg-[#0088B5] text-white mt-3" data-testid="client-start-conversation-btn">
                  <Plus className="h-4 w-4 mr-1" /> Contacter l'equipe
                </Button>
              )}
            </div>
          ) : (
            filteredConvos.map(convo => (
              <button key={convo.id} onClick={() => openConversation(convo)}
                className={`w-full text-left p-4 border-b border-[#2A3441]/50 hover:bg-[#1a2236] transition-colors ${
                  activeConvo?.id === convo.id ? "bg-[#1a2236] border-l-2 border-l-[#0099CC]" : ""
                }`} data-testid={`conversation-${convo.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white text-sm font-medium truncate">{convo.client_name || "Client"}</span>
                  <div className="flex items-center gap-2">
                    {convo[unreadKey] > 0 && (
                      <Badge className="bg-[#0099CC] text-white text-xs h-5 min-w-[20px] flex items-center justify-center rounded-full px-1.5">
                        {convo[unreadKey]}
                      </Badge>
                    )}
                    <span className="text-[#64748b] text-xs flex-shrink-0">{formatDate(convo.last_message_at)}</span>
                  </div>
                </div>
                <p className="text-[#64748b] text-xs truncate">{convo.last_message || "Pas encore de messages"}</p>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Main Thread */}
      <div className={`${!mobileShowThread ? "hidden md:flex" : "flex"} flex-col flex-1 min-w-0`}>
        {!activeConvo ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="h-16 w-16 text-[#2A3441] mx-auto mb-4" />
              <p className="text-[#64748b]">Selectionnez une conversation</p>
            </div>
          </div>
        ) : (
          <>
            {/* Thread Header */}
            <div className="flex items-center gap-3 p-4 border-b border-[#2A3441] flex-shrink-0">
              <Button variant="ghost" size="sm" onClick={() => setMobileShowThread(false)} className="md:hidden text-[#94A3B8]">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="w-9 h-9 rounded-full bg-[#0099CC]/20 flex items-center justify-center">
                <User className="h-5 w-5 text-[#0099CC]" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-sm">{activeConvo.client_name || "Client"}</h2>
                <p className="text-[#64748b] text-xs">{messages.length} messages</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0" data-testid="messages-thread">
              {msgLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#0099CC]" /></div>
              ) : messages.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-[#64748b] text-sm">Envoyez le premier message</p>
                </div>
              ) : (
                messages.map(msg => {
                  const isMine = (isAdmin && msg.sender_role === "admin") || (!isAdmin && msg.sender_role === "client");
                  return (
                    <div key={msg.id} className={`flex gap-3 ${isMine ? "flex-row-reverse" : ""}`} data-testid={`dm-${msg.id}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.sender_role === "admin" ? "bg-emerald-500/20" : "bg-[#0099CC]/20"
                      }`}>
                        {msg.sender_role === "admin" ? <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> : <User className="h-3.5 w-3.5 text-[#0099CC]" />}
                      </div>
                      <div className={`max-w-[70%] rounded-xl px-4 py-2.5 ${
                        isMine ? "bg-[#0099CC]/10 border border-[#0099CC]/30" : "bg-[#151B2B] border border-[#2A3441]"
                      }`}>
                        <p className="text-[#e2e8f0] text-sm whitespace-pre-wrap">{msg.content}</p>
                        {msg.attachments?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {msg.attachments.map((att, i) => (
                              <a key={i} href={`${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}${att.url}`} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs bg-[#0B0F19] rounded px-2 py-1 text-[#0099CC] hover:text-white">
                                <FileIcon className="h-3 w-3" /> {att.filename} <Download className="h-3 w-3" />
                              </a>
                            ))}
                          </div>
                        )}
                        <span className={`text-[#64748b] text-[10px] block mt-1 ${isMine ? "text-right" : ""}`}>{formatFullDate(msg.created_at)}</span>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="flex-shrink-0 border-t border-[#2A3441] p-4" data-testid="dm-input-area">
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {attachments.map((att, i) => (
                    <div key={i} className="flex items-center gap-1 text-xs bg-[#0B0F19] border border-[#2A3441] rounded-lg px-2 py-1 text-[#94A3B8]">
                      <FileIcon className="h-3 w-3" /> {att.filename}
                      <button onClick={() => removeAttachment(i)} className="text-red-400 hover:text-red-300 ml-1"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2 items-end">
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} data-testid="dm-file-input" />
                <Button variant="ghost" size="icon" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  className="text-[#64748b] hover:text-white flex-shrink-0 h-9 w-9" data-testid="dm-attach-btn">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                </Button>
                <Textarea value={newMessage} onChange={(e) => setNewMessage(e.target.value)} onKeyDown={handleKeyDown}
                  placeholder="Ecrire un message..." rows={1}
                  className="bg-[#0B0F19] border-[#2A3441] text-white resize-none flex-1 min-h-[36px]"
                  data-testid="dm-message-input" />
                <Button onClick={handleSend} disabled={sending || (!newMessage.trim() && attachments.length === 0)}
                  className="bg-[#0099CC] hover:bg-[#0088B5] text-white flex-shrink-0 h-9 w-9 p-0" data-testid="dm-send-btn">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* New Conversation Dialog */}
      <Dialog open={newConvoDialog} onOpenChange={setNewConvoDialog}>
        <DialogContent className="bg-[#151B2B] border-[#2A3441] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Nouvelle conversation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="select-client-convo">
                <SelectValue placeholder="Selectionnez un client" />
              </SelectTrigger>
              <SelectContent className="bg-[#151B2B] border-[#2A3441] max-h-60">
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setNewConvoDialog(false)} className="flex-1 border-[#2A3441] text-[#94A3B8]">
                Annuler
              </Button>
              <Button onClick={handleNewConvo} disabled={!selectedClient} className="flex-1 bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="create-convo-btn">
                Demarrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
